import {
    type DeepPartial,
    InvestmentManagementAccountType,
    MoneyMapAccountStatus,
    MoneyMapAccountType,
    type MoneyMapAccountWithAccountDetails,
    type MoneyMapApexAccountCreateParameters,
    type MoneyMapApexAccountDetails,
    type MoneyMapIMAccountDetails,
    SupportedApexAccountRegistrationType,
} from '@fruitful/types';

export interface BackdoorRothRuleSplit {
    isBackdoorRoth: boolean;
    /** Roth IRA — shown in primary "Account settings" (only when `isBackdoorRoth`). */
    rothAccount?: DeepPartial<MoneyMapAccountWithAccountDetails>;
    /** Traditional/Rollover IRA — shown in "Account for income split" (only when `isBackdoorRoth`). */
    tradAccount?: DeepPartial<MoneyMapAccountWithAccountDetails>;
}

/**
 * API stores the rule’s primary MMA on the Trad row with nested `backdoorRothLinkedMoneyMapAccount` (Roth).
 * Goal and other-account forms edit Roth in "Account settings" and Trad in the transfer / Trad panel.
 */
export const splitBackdoorRothRuleAccounts = (
    moneyMapAccount: DeepPartial<MoneyMapAccountWithAccountDetails> | undefined,
    accounts: MoneyMapAccountWithAccountDetails[],
): BackdoorRothRuleSplit => {
    if (moneyMapAccount == null) {
        return { isBackdoorRoth: false };
    }

    const rothNested = moneyMapAccount.backdoorRothLinkedMoneyMapAccount;
    const rothId = moneyMapAccount.backdoorRothLinkedMoneyMapAccountId;

    const rothFromList =
        rothId != null ? accounts.find((a) => a.id === rothId) : undefined;

    const rothStub: DeepPartial<MoneyMapAccountWithAccountDetails> | undefined =
        rothId != null && rothId !== '' ? { id: rothId } : undefined;

    const rothAccount = rothNested ?? rothFromList ?? rothStub;

    // Nested Roth, or linked by ID (including before `accounts` hydrates the Roth row).
    const isBackdoor = rothNested != null || (rothId != null && rothId !== '');

    if (!isBackdoor) {
        return { isBackdoorRoth: false };
    }

    return {
        isBackdoorRoth: true,
        tradAccount: moneyMapAccount,
        rothAccount:
            rothAccount as DeepPartial<MoneyMapAccountWithAccountDetails>,
    };
};

export const isApexAccountRothRegistration = (
    account: DeepPartial<MoneyMapAccountWithAccountDetails> | undefined,
    apexInvestmentAccountTypeFromForm?: string,
): boolean => {
    if (
        apexInvestmentAccountTypeFromForm != null &&
        apexInvestmentAccountTypeFromForm !== ''
    ) {
        return (
            apexInvestmentAccountTypeFromForm ===
            SupportedApexAccountRegistrationType.RegistrationIraRoth
        );
    }
    if (account?.type !== MoneyMapAccountType.APEX_INVESTMENT_MANAGEMENT) {
        return false;
    }
    const apexType = (
        account.accountDetails as {
            apexAccountType?: SupportedApexAccountRegistrationType;
        }
    )?.apexAccountType;
    const fromCreate = (
        account.createParameters as
            | MoneyMapApexAccountCreateParameters
            | undefined
    )?.apexAccountType;
    return (
        apexType === SupportedApexAccountRegistrationType.RegistrationIraRoth ||
        fromCreate === SupportedApexAccountRegistrationType.RegistrationIraRoth
    );
};

export const isBackdoorTradEligibleApexType = (
    apexType: string | undefined,
): boolean =>
    apexType ===
        SupportedApexAccountRegistrationType.RegistrationIraTraditional ||
    apexType === SupportedApexAccountRegistrationType.RegistrationIraRollover;

/** Apex Trad or Rollover IRA (proposed or existing). */
export const isBackdoorTradEligibleApexMoneyMapAccount = (
    account: MoneyMapAccountWithAccountDetails,
): boolean => {
    if (account.type !== MoneyMapAccountType.APEX_INVESTMENT_MANAGEMENT) {
        return false;
    }
    const apexType = (account.accountDetails as MoneyMapApexAccountDetails)
        .apexAccountType;
    return isBackdoorTradEligibleApexType(apexType);
};

/**
 * Orion IM account usable as the Traditional IRA leg: only **IRA** type (Rollover is represented on Apex, not IM).
 */
export const isBackdoorTradEligibleImMoneyMapAccount = (
    account: MoneyMapAccountWithAccountDetails,
): boolean => {
    if (account.type !== MoneyMapAccountType.INVESTMENT_MANAGEMENT) {
        return false;
    }
    const imType = (account.accountDetails as MoneyMapIMAccountDetails)
        .accountType;
    return imType === InvestmentManagementAccountType.IRA;
};

export const isBackdoorTradEligibleMoneyMapAccount = (
    account: MoneyMapAccountWithAccountDetails,
): boolean =>
    isBackdoorTradEligibleApexMoneyMapAccount(account) ||
    isBackdoorTradEligibleImMoneyMapAccount(account);

/**
 * Existing (non-proposed) Trad IRA selected for backdoor: show ops guidance about goals and balance.
 */
export const shouldWarnBackdoorTradExistingAccount = (
    account: MoneyMapAccountWithAccountDetails | undefined,
): boolean => {
    if (account?.status !== MoneyMapAccountStatus.EXISTS) {
        return false;
    }
    return isBackdoorTradEligibleMoneyMapAccount(account);
};

/**
 * True when the account is already tagged as the Traditional leg of a backdoor Roth pair,
 * detected from either the money-map relationship or the account-system `backdoorRothRole`.
 */
export const isAccountBackdoorTrad = (
    account: MoneyMapAccountWithAccountDetails,
): boolean =>
    (account.backdoorRothLinkedMoneyMapAccountId != null &&
        account.backdoorRothLinkedMoneyMapAccountId !== '') ||
    (account.accountDetails as MoneyMapApexAccountDetails)?.backdoorRothRole ===
        'TRADITIONAL';

/**
 * True when the account is already tagged as the Roth leg of a backdoor Roth pair,
 * detected from account-system `backdoorRothRole` or a money-map Trad linking to it.
 */
export const isAccountBackdoorRoth = (
    account: MoneyMapAccountWithAccountDetails,
    accounts: MoneyMapAccountWithAccountDetails[],
): boolean =>
    (account.accountDetails as MoneyMapApexAccountDetails)?.backdoorRothRole ===
        'ROTH' ||
    accounts.some((a) => a.backdoorRothLinkedMoneyMapAccountId === account.id);

/**
 * When the selected account is a Roth IRA that is NOT already part of a backdoor pair,
 * returns any existing Traditional/Rollover IRA accounts owned by the same member
 * that are also unlinked. Used to prompt guides to link them via #im-squad-pii.
 */
export const findUnlinkedTradIrasForRoth = (
    selectedAccount: MoneyMapAccountWithAccountDetails | undefined,
    accounts: MoneyMapAccountWithAccountDetails[],
): MoneyMapAccountWithAccountDetails[] => {
    if (selectedAccount == null) {
        return [];
    }
    if (!isApexAccountRothRegistration(selectedAccount)) {
        return [];
    }
    if (isAccountBackdoorRoth(selectedAccount, accounts)) {
        return [];
    }
    const rothOwnerId = selectedAccount.accountDetails.primaryUserId;
    return accounts.filter(
        (a) =>
            a.id !== selectedAccount.id &&
            a.status === MoneyMapAccountStatus.EXISTS &&
            isBackdoorTradEligibleMoneyMapAccount(a) &&
            !isAccountBackdoorTrad(a) &&
            (rothOwnerId == null ||
                a.accountDetails.primaryUserId === rothOwnerId),
    );
};

/**
 * Reverse of `findUnlinkedTradIrasForRoth`: when a Traditional/Rollover IRA is selected
 * that is NOT already paired, returns any existing Roth IRAs owned by the same member
 * that are also unlinked. Used to prompt guides about backdoor Roth as an option.
 */
export const findUnlinkedRothIrasForTrad = (
    selectedAccount: MoneyMapAccountWithAccountDetails | undefined,
    accounts: MoneyMapAccountWithAccountDetails[],
): MoneyMapAccountWithAccountDetails[] => {
    if (selectedAccount == null) {
        return [];
    }
    if (!isBackdoorTradEligibleMoneyMapAccount(selectedAccount)) {
        return [];
    }
    if (isAccountBackdoorTrad(selectedAccount)) {
        return [];
    }
    const tradOwnerId = selectedAccount.accountDetails.primaryUserId;
    return accounts.filter(
        (a) =>
            a.id !== selectedAccount.id &&
            a.status === MoneyMapAccountStatus.EXISTS &&
            isApexAccountRothRegistration(a) &&
            !isAccountBackdoorRoth(a, accounts) &&
            (tradOwnerId == null ||
                a.accountDetails.primaryUserId === tradOwnerId),
    );
};
